/**
 * cartAttachmentsSlice — per-cart-item file attachments.
 *
 * Stores visual upload metadata (gc filename + signed URL) for cart items
 * that require uploads at product-pick time (i.e. staging products in the
 * Product Drawer). Keyed by `CartItemId`.
 *
 * Design decision p55-6-01: option B — sibling slice, keeps `cartSlice` clean.
 * Only staging uses this in v1; other upload-in-drawer flows can extend the
 * `AttachmentKind` union when they land.
 *
 * This slice is NOT persisted in `OrderDraftDTO` — uploaded files are tied to
 * the legacy `sessionId` which is regenerated on draft resume. The attachment
 * metadata here is for in-session drawer re-open pre-population only.
 *
 * When a cart item is removed (`removeItem`), the corresponding attachments
 * are cleared in `cartSlice.removeItem` via the store composition
 * (see `cartSlice.ts`). This slice just holds the data.
 */

import type { StateCreator } from 'zustand'

import type { CartItemId } from 'models/purchaseFlowRevamp'
import type { PfStoreState } from '../store'

export type AttachmentKind = 'staging-visual'

export interface StagingVisualAttachment {
  kind: 'staging-visual'
  /**
   * GCS filename returned by the upload saga after the file is stored.
   * Used to show uploaded images when the drawer is re-opened for the same
   * cart item.
   */
  gcFilename: string
  /** Signed URL for displaying the thumbnail (may be null until resolved). */
  signedUrl: string | null
  /** Original filename for display purposes. */
  originalFilename: string
}

export type CartAttachment = StagingVisualAttachment

export interface CartAttachmentsSliceState {
  /**
   * Attachments keyed by CartItemId. Each entry is an ordered list of
   * attachments added for that cart item.
   */
  attachments: Record<CartItemId, CartAttachment[]>
  /**
   * The client's own name for each uploaded file, by stored filename. The backend names every file
   * `<sessionId>-backbonephoto-00N` and its listings return only that, so this is what a resumed draft shows.
   */
  uploadDisplayNames: Record<string, string>
}

export interface CartAttachmentsSliceActions {
  /**
   * Adds a single attachment for the given cart item.
   * Appends to the existing list; does not deduplicate.
   */
  addAttachment(itemId: CartItemId, attachment: CartAttachment): void

  /**
   * Removes a single attachment by gc filename for the given cart item.
   * No-op if the item or attachment is not found.
   */
  removeAttachment(itemId: CartItemId, gcFilename: string): void

  /**
   * Drops `gcFilename` from whichever line owns it, without the caller having to
   * know which one that is. The upload listing is keyed by file, not by line, so
   * a deletion in the drawer can only name the file.
   */
  unclaimAttachment(gcFilename: string): void

  /**
   * Replaces the full attachment list for a cart item.
   * Used when re-opening the drawer shows updated attachments.
   */
  setAttachments(itemId: CartItemId, attachments: CartAttachment[]): void

  /**
   * Clears all attachments for a cart item. Called when the item is removed
   * from the cart or the cart is cleared.
   */
  clearAttachments(itemId: CartItemId): void

  /** Clears all attachments for all cart items. Called on `clearCart`. */
  clearAllAttachments(): void

  /** Records the client's names for stored files. A no-op when every name is already known. */
  rememberUploadNames(names: Record<string, string>): void
}

export type CartAttachmentsSlice = CartAttachmentsSliceState & CartAttachmentsSliceActions

export const createCartAttachmentsSlice: StateCreator<
  PfStoreState,
  [],
  [],
  CartAttachmentsSlice
> = (set) => ({
  attachments: {},
  uploadDisplayNames: {},

  rememberUploadNames: (names) =>
    set((state) => {
      const fresh = Object.entries(names).filter(([stored, name]) => state.uploadDisplayNames[stored] !== name)
      if (fresh.length === 0) return state
      return { uploadDisplayNames: { ...state.uploadDisplayNames, ...Object.fromEntries(fresh) } }
    }),

  addAttachment: (itemId, attachment) =>
    set((state) => ({
      attachments: {
        ...state.attachments,
        [itemId]: [...(state.attachments[itemId] ?? []), attachment],
      },
    })),

  removeAttachment: (itemId, gcFilename) =>
    set((state) => {
      const current = state.attachments[itemId]
      if (!current) return state
      const next = current.filter((a) => a.gcFilename !== gcFilename)
      return {
        attachments: {
          ...state.attachments,
          [itemId]: next,
        },
      }
    }),

  unclaimAttachment: (gcFilename) =>
    set((state) => {
      const owner = Object.entries(state.attachments)
        .find(([, list]) => list.some((a) => a.gcFilename === gcFilename))
      // Returning a fresh object unconditionally would re-fingerprint the
      // persisted slice (see `autoSave.selectPersistedFingerprint`) and save on
      // every delete click, hit or miss.
      if (!owner) return state
      const [itemId, list] = owner
      return {
        attachments: {
          ...state.attachments,
          [itemId]: list.filter((a) => a.gcFilename !== gcFilename),
        },
      }
    }),

  setAttachments: (itemId, attachments) =>
    set((state) => ({
      attachments: {
        ...state.attachments,
        [itemId]: attachments,
      },
    })),

  clearAttachments: (itemId) =>
    set((state) => {
      const { [itemId]: _removed, ...rest } = state.attachments
      return { attachments: rest }
    }),

  clearAllAttachments: () => set({ attachments: {} }),
})

/**
 * Filenames already CLAIMED by committed cart lines other than `exceptItemId`.
 *
 * The upload scope is grouped per product, which is what a drawer needs while it
 * is open: a client switching variant inside one product must keep seeing their
 * files. But the moment a line is added to the cart it OWNS the files it was
 * configured with, so the next drawer for that same product must start clean
 * instead of re-offering — and re-attaching — the previous line's photos.
 *
 * A claim is exactly the line's attachment snapshot, which is also what becomes
 * the order's `inputImages`, so "claimed" and "already on an order line" cannot
 * drift apart.
 *
 * `exceptItemId` is the line being EDITED: its own files must stay visible, or
 * re-opening a configured line would show an empty dropzone and drop its photos
 * on save.
 */
export const claimedFilenames = (
  attachments: Record<CartItemId, CartAttachment[]>,
  exceptItemId?: CartItemId | null
): ReadonlySet<string> => {
  const claimed = new Set<string>()
  for (const [itemId, list] of Object.entries(attachments)) {
    if (exceptItemId && itemId === exceptItemId) continue
    for (const attachment of list) claimed.add(attachment.gcFilename)
  }
  return claimed
}
